import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { hasPermission } from '@/composables/usePermission'

export default function useWorkerList() {
  const { showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItems } = useLambda()
  const { workersQueryWithOffset } = useMongoDBFunctions()
  const refWorkerListTable = ref(null)
  const workers = ref([])
  const {
    // eslint-disable-next-line camelcase
    role, worker_id = null, client = null,
  } = store.state?.userStore?.userData

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions')
      },
      {
        key: 'name',
        label: i18n.t('message.tableHeader.name'),
        sortable: true,
      },
      {
        key: 'email',
        label: i18n.t('message.tableHeader.email'),
        sortable: true,
        tdClass: 'small',
      },
      {
        key: 'companyData',
        label: i18n.t('label.Company'),
        sortable: true,
      },
      {
        key: 'rolesData',
        label: i18n.t('label.Roles'),
      },
      {
        key: 'privileges',
        label: i18n.t('label.Privileges'),
        sortable: true,
      },
      {
        key: 'hasCustomPermissions',
        label: i18n.t('message.worker_custom_permissions_column'),
        thClass: 'worker-custom-permissions-header',
      },
    ]
  })
  const perPage = ref(10)
  const totalWorkers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const locationQuery = ref('')
  const locations = ref([])
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const statusFilter = ref(null)
  const isLoading = ref(true)

  const locationFilter = ref([]);
  const roleFilter = ref([]);
  const privilegeFilter = ref([]);
  const companyFilter = ref([]);

  const refetchData = () => {
    if (refWorkerListTable.value) {
      refWorkerListTable.value.refresh()
    }
  }

  const excelFields = ref({
    [i18n.t('message.tableHeader.name')]: "name",
    [i18n.t('Email')]: "email",
    [i18n.t('label.Company')]: "companyData",
    [i18n.t('label.Locations')]: "locationsData",
    [i18n.t('label.Privileges')]: "privileges",
    [i18n.t('label.Roles')]: "rolesData",
    [i18n.t('message.tableHeader.supervisor')]: "supervisorsData",
  })

  const workersTotal = ref([])

  // const getLocations = () => {
  //   store
  //     .dispatch('organization-worker/getLocations', { query: locationsQuery, variables: { query: { deleted: false } } })
  //     .then(response => {
  //       locations.value = response.data?.data?.locations?.map(({ location, _id }) => ({ name: location, value: _id })) || []
  //     })
  //     .catch(err => {
  //       console.log(err)
  //     })
  // }

  const fetchWorkers = async () => {
    isLoading.value = true

    try {
      let supervisor = ''
      let input = {}
      
      if (role === 'supervisor') {
        supervisor = worker_id.$oid
        input = {
          limit: perPage.value,
          total: currentPage.value === 1,
          offset: (currentPage.value * perPage.value) - perPage.value,
          query: {},
          search: searchQuery.value,
          sortBy: sortBy.value,
          sortOrder: isSortDirDesc.value ? -1 : 1,
          location: locationFilter.value,
          roles: roleFilter.value,
          privileges: privilegeFilter.value,
          company: companyFilter.value,
          supervisor,
          client_id: (role !== 'admin') ? client.$oid : null,
        }
      } else {
        input = {
          limit: perPage.value,
          total: currentPage.value === 1,
          offset: (currentPage.value * perPage.value) - perPage.value,
          query: {},
          search: searchQuery.value,
          sortBy: sortBy.value,
          sortOrder: isSortDirDesc.value ? -1 : 1,
          location: locationFilter.value,
          roles: roleFilter.value,
          privileges: privilegeFilter.value,
          company: companyFilter.value,
          client_id: (role !== 'admin') ? client.$oid : null,
        }
      }

      const { workers: w, workersTotal: z, totalRecords: total } = await workersQueryWithOffset(input)
    
      workers.value = w

      const modifiedWorkers = z.map(worker => {
        let location = ""
        let roles = ""
        let supervisors = ""
        let companies = ""
        if(worker.locationsData.length >= 2){
          location = worker.locationsData.map(location => location.location).join(" / ")
        } else {
          location = worker.locationsData[0]?.location || "";
        }
        if(worker.rolesData.length >= 2){
          roles = worker.rolesData.map(role => role.name).join(" / ")
        } else {
          roles = worker.rolesData[0]?.name || "";
        }
        if(worker.supervisorsData.length >= 2){
          supervisors = worker.supervisorsData.map(supervisor => supervisor.name).join(" / ")
        } else {
          supervisors = worker.supervisorsData[0]?.name || "";
        }
        if(worker.companyData && worker.companyData.length >= 2){
          companies = worker.companyData.map(company => company.name).join(" / ")
        } else {
          companies = worker.companyData && worker.companyData[0]?.name || "";
        }
        return {
          ...worker,
          locationsData: location,
          rolesData: roles,
          supervisorsData: supervisors,
          companyData: companies
        };
      });

      workersTotal.value = modifiedWorkers
      if (currentPage.value === 1) totalWorkers.value = total
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_worker_list') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteWorker = async (userId, callback) => {
    if (!hasPermission('organization-workers', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      callback(false)
      return
    }

    try {
      const query = { _id: oid(userId), client_id: oid(client?.$oid) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection: 'worker', query, action })

      // Check if worker is an existing user and soft delete it
      // const userQuery = { 'custom_data.worker_id': oid(userId) }
      // await updateItem({ collection: 'user', query: userQuery, action })

      callback(true)

      // Check if worker supervises other workers and remove it from the list of those workers' supervisors
      const supervisorQuery = { supervisors: oid(userId) }
      const items = await getItems({ collection: 'worker', query: supervisorQuery })

      items?.forEach(worker => {
        if (worker?.supervisors?.length) {
          const supervisorsFiltered = worker.supervisors.filter(e => e.toString() !== userId.toString())

          try {
            updateItem({ collection: 'worker', query: { _id: oid(worker._id) }, action: { $set: { supervisors: supervisorsFiltered.map(e => oid(e)) } } })
          } catch (error) {
            console.log(error)
          }
        }
      })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  watch([currentPage, perPage, statusFilter], () => {
    fetchWorkers()
  })

  watch([locationQuery, searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchWorkers()
  })

  return {
    fetchWorkers,
    tableColumns,
    perPage,
    currentPage,
    totalWorkers,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refWorkerListTable,
    workers,
    statusFilter,
    refetchData,
    deleteWorker,
    locationQuery,
    locations,
    // getLocations,
    locationFilter,
    roleFilter,
    privilegeFilter,
    companyFilter,
    isLoading,
    role,
    excelFields,
    workersTotal
  }
}
